import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { VerticaLinkedService } from 'app/model/linkedservice/vertica.linkedservice';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel, parameterizablePasswordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, ConnectionAuthKind, ITridentResource } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { ValueAdapter } from '../value.adapter';
import { connectionStringPartToModel, connectionStringPartToResource } from 'app/shared/utils/linkedservice-parameter.util';

const verticaReservedKeys = ['server', 'pwd', 'port', 'database', 'uid', 'connectionString', 'encryptedCredential'];

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(VerticaLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: VerticaLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                        // won't cover fully by json for now
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                pwd: 'secret',
                encryptedCredential: '*',
                server: '*',
                port: '*',
                database: '*',
                uid: '*'
            },
            parameters: '*'
        }
    }
})
export class VerticaLinkedServiceAdapter extends LinkedServiceAdapter<VerticaLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => VerticaLinkedService {
        return VerticaLinkedService;
    }

    protected model(): IModel {
        return VerticaLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.VerticaLinkedServiceTypeProperties => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Basic:
                const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
                    tridentResource.datasourceReference
                );
                const path = datasourceReference.path;
                const [server, database] = path.split(';');
                return {
                    server,
                    database
                };
            default:
                return undefined;
        }
    };

    protected async propertiesToResourceAsync(linkedService: VerticaLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.VerticaLinkedService;
        properties.type = Metamodel.modelFor(VerticaLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            // Vertica V2
            properties.version = '2.0';
            const results: Record<string, string | boolean | number> = {};
            linkedService.additionalFields?.forEach((pair) => {
                if (pair.value != null && !verticaReservedKeys.includes(pair.key)) {
                    results[pair.key] = pair.value;
                }
            });

            properties.typeProperties = {
                ...results,
                server: this.valueToResource(linkedService, VerticaLinkedService.SERVER),
                port: this.valueToResource(linkedService, VerticaLinkedService.PORT),
                pwd: parameterizablePasswordOrAKVtoSecretBase(linkedService, VerticaLinkedService.PASSWORD, this.context),
                uid: this.valueToResource(linkedService, VerticaLinkedService.USERNAME),
                database: this.valueToResource(linkedService, VerticaLinkedService.DATABASE),
            };
            return linkedServiceResource;
        }

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', connectionStringPartToResource(linkedService, VerticaLinkedService.SERVER));
            builder.add('Port', connectionStringPartToResource(linkedService, VerticaLinkedService.PORT));
            builder.add('Database', connectionStringPartToResource(linkedService, VerticaLinkedService.DATABASE));
            builder.add('UID', connectionStringPartToResource(linkedService, VerticaLinkedService.USERNAME));
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('PWD', connectionStringPartToResource(linkedService, VerticaLinkedService.PASSWORD));
            }
            linkedService.additionalFields?.forEach((pair) => {
                builder.add(pair.key, pair.value || '');
            });
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            pwd: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: VerticaLinkedService): Promise<VerticaLinkedService> {
        const properties = linkedServiceResource.properties as Model.VerticaLinkedService;
        const typeProperties = properties.typeProperties;

        linkedService.server = undefined;
        linkedService.port = undefined;
        linkedService.databaseName = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;
        linkedService.version = properties.version ? driverVersions.v2 : driverVersions.v1;
        if (__IS_FABRIC_DPE__) {
            linkedService.version = driverVersions.v2;
        }
        linkedService.isLegacyConnector = linkedService.version !== driverVersions.v2;
        if (properties.version) {
            ValueAdapter.toModel(linkedService, VerticaLinkedService.SERVER, typeProperties.server);
            ValueAdapter.toModel(linkedService, VerticaLinkedService.PORT, typeProperties.port);
            ValueAdapter.toModel(linkedService, VerticaLinkedService.DATABASE, typeProperties.database);
            ValueAdapter.toModel(linkedService, VerticaLinkedService.USERNAME, typeProperties.uid);
            parameterizableSecretBaseToModel(linkedService, VerticaLinkedService.PASSWORD, typeProperties.pwd, this.context);

            linkedService.additionalFields = [];
            for (const [key, value] of Object.entries(typeProperties)) {
                if (!verticaReservedKeys.includes(key)) {
                    linkedService.additionalFields.push({
                        key,
                        value
                    });
                }
            }

            return linkedService;
        }

        VerticaLinkedService.SERVER.setValue(linkedService, undefined);
        VerticaLinkedService.SERVER.setTemplateValue(linkedService, undefined);
        VerticaLinkedService.DATABASE.setValue(linkedService, undefined);
        VerticaLinkedService.DATABASE.setTemplateValue(linkedService, undefined);
        VerticaLinkedService.USERNAME.setValue(linkedService, undefined);
        VerticaLinkedService.USERNAME.setTemplateValue(linkedService, undefined);
        VerticaLinkedService.PASSWORD.setValue(linkedService, undefined);
        VerticaLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
        VerticaLinkedService.PORT.setValue(linkedService, undefined);
        VerticaLinkedService.PORT.setTemplateValue(linkedService, undefined);
        if (isString(typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService();
            builder.parse(typeProperties.connectionString as string);

            connectionStringPartToModel(
                linkedService,
                VerticaLinkedService.SERVER,
                builder.getValue('Server', false) || ''
            );
            connectionStringPartToModel(
                linkedService,
                VerticaLinkedService.PORT,
                builder.getValue('Port', false) || ''
            );
            connectionStringPartToModel(
                linkedService,
                VerticaLinkedService.DATABASE,
                builder.getValue('Database', false) || ''
            );
            connectionStringPartToModel(
                linkedService,
                VerticaLinkedService.USERNAME,
                builder.getValue('UID', false) || ''
            );
            connectionStringPartToModel(
                linkedService,
                VerticaLinkedService.PASSWORD,
                builder.getValue('PWD', false) || '',
                true
            );
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'PWD']);
            if (typeProperties.pwd) {
                parameterizableSecretBaseToModel(linkedService, VerticaLinkedService.PASSWORD, typeProperties.pwd, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(typeProperties.connectionString)) {
            linkedService.server = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            if (typeProperties.pwd) {
                parameterizableSecretBaseToModel(linkedService, VerticaLinkedService.PASSWORD, typeProperties.pwd, this.context);
            }
        } else if (typeProperties.connectionString
            && (typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
